import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { LuCheck, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { SELECT_STATES, SELECT_STYLES, selectTriggerVariants } from "./constants";
import {
  SelectTriggerProps,
  SelectContentProps,
  SelectLabelProps,
  SelectItemProps,
  SelectSeparatorProps,
  SelectScrollUpButtonProps,
  SelectScrollDownButtonProps,
  SelectProps,
} from "./types";

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      className,
      children,
      state = SELECT_STATES.NORMAL,
      errorMessage,
      icon,
      iconClassName,
      showDefaultArrow = true,
      variant,
      size,
      placeholder,
      contentAlign = "center",
      ...props
    },
    ref
  ) => (
    <div className="w-full">
      <SelectPrimitive.Trigger
        ref={ref}
        style={{
          zIndex: '10000'
        }}
        className={cn(
          selectTriggerVariants({ variant, size }),
          state === SELECT_STATES.ERROR && "border-red-500 focus:ring-red-500",
          icon ? "pl-10" : "",
          className
        )}
        {...props}
      >
        <div
          className={cn(
            "flex justify-between w-full h-full",
            contentAlign === "start" ? "items-start pt-2.5" : "items-center"
          )}
        >
          {icon && (
            <span
              className={cn(
                "absolute left-3 flex h-5 w-5 items-center justify-center",
                contentAlign === "start"
                  ? "top-4"
                  : "top-1/2 -translate-y-1/2",
                iconClassName
              )}
            >
              {icon}
            </span>
          )}
          <div className="flex flex-col items-start justify-center flex-1 min-w-0 overflow-hidden">
            {placeholder && (
              <p className="line-clamp-1 text-left text-[12px] font-medium text-ticketon-dark-blue">
                {placeholder}
              </p>
            )}
            <div className="w-full text-left text-[12px] font-normal [&>span]:block [&>span]:w-full [&>span]:truncate [&>span]:text-[12px] [&>span]:font-normal">
              {children}
            </div>
          </div>
          <div
            className={cn(
              "flex flex-shrink-0 ml-1",
              contentAlign === "start" ? "items-start pt-0.5" : "items-center"
            )}
          >
            {showDefaultArrow && (
              <SelectPrimitive.Icon asChild>
                <LuChevronDown className="h-5 w-5 text-primary" />
              </SelectPrimitive.Icon>
            )}
          </div>
        </div>
      </SelectPrimitive.Trigger>
      {state === SELECT_STATES.ERROR && errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  )
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  SelectScrollUpButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(SELECT_STYLES.scrollButton, className)}
    {...props}
  >
    <LuChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  SelectScrollDownButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(SELECT_STYLES.scrollButton, className)}
    {...props}
  >
    <LuChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        SELECT_STYLES.content,
        position === "popper" && SELECT_STYLES.popperPosition,
        className
      )}
      position={position}
      sideOffset={4}
      side="bottom"
      align="center"
      style={{
        minWidth: "var(--radix-select-trigger-width)",
        width: "var(--radix-select-trigger-width)",
        ...props.style,
      }}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          SELECT_STYLES.viewport,
          position === "popper" && SELECT_STYLES.popperViewport
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  SelectLabelProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(SELECT_STYLES.label, className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(SELECT_STYLES.item, className)}
    {...props}
  >
    <div className="flex items-center justify-between w-full">
      <SelectPrimitive.ItemText asChild>
        <span className="flex-grow truncate">{children}</span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        <LuCheck className="h-4 w-4 text-primary ml-2" />
      </SelectPrimitive.ItemIndicator>
    </div>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  SelectSeparatorProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(SELECT_STYLES.separator, className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const Select: React.FC<SelectProps> = ({
  label,
  labelClassName,
  containerClassName,
  floatingLabel,
  children,
  ...props
}) => {
  return (
    <div className={cn("w-full relative", containerClassName)}>
      {label && !floatingLabel && (
        <label
          className={cn(
            "mb-1 block text-sm font-medium text-primary",
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>
      {label && floatingLabel && (
        <label className={cn(SELECT_STYLES.floatingLabel, labelClassName)}>
          {label}
        </label>
      )}
    </div>
  );
};
Select.displayName = "Select";

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
